import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { useUserRecordActivityUpdate } from "../../../components/Records/References/ReferencesComponents/SelectableReferences/useUpdateUserRecordActivity";
import {
  handleAggregationUpdates,
  handleUpdatedRecords,
  handleUpdateUserRecordRecommendedActivity,
} from "../../../utils/RecordUtils";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";
import TenantAPI from "../../api/TenantAPI";

interface MutationProps {
  field: any;
  recordID: string;
  payload: any;
  subitemID?: string;
  moduleAPIName: string;
  _UserActivity: any;
  originalRecord?: Record<string, any>; // ONLY needed when performing optimistic updates.
  onSuccess?: () => void;
  onError?: () => void;
  _FlowChartID?: string;
}

/**
 * ------Updating MultiSubitems------
 * To update more than one subitems
 * {
 * "MultiSubitem": [
 *  {
 *       "id": "uuid_1",
 *     "FieldToUpdate": "Value"
 *   },
 *   {
 *       "id": "uuid_2",
 *     "FieldToUpdate": "Value"
 *   }
 * ]
 *}
 *
 */

/**
 * useUpdateRecord - RQ hook for updating a record in a moudle
 *
 * @param {bool=} useOptimisticUpdate - Optional, whether or not to use optimistic update
 */
const useUpdateRecord = (
  useOptimisticUpdate = false,
  theRecordID: string | undefined
) => {
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);
  const isSupportal = window.location.pathname.startsWith("/supportal");
  const { updateUserRecordActivityCache } = useUserRecordActivityUpdate();

  const handleSubitem = (
    field: any,
    payload: any,
    subitemID?: string,
    _UserActivity?: any,
    _FlowChartID?: string
  ) => {
    const subitemParent = field.SubitemParent;

    if (!subitemParent) return { payload, _UserActivity, _FlowChartID };

    const finalPayload = {
      [subitemParent]: {
        ...payload,
      },
    };

    // For MultiSubitem, provide the subitemID so we can update the correct subitem
    if (subitemID) {
      finalPayload[subitemParent].id = subitemID;
    }

    return { payload: finalPayload, _UserActivity, _FlowChartID };
  };

  const updateRecord = useMutation({
    mutationFn: ({
      field,
      recordID,
      payload,
      subitemID,
      moduleAPIName,
      _UserActivity,
      _FlowChartID,
    }: MutationProps) => {
      const finalPayload = handleSubitem(
        field,
        payload,
        subitemID,
        _UserActivity,
        _FlowChartID
      );

      if (field?.ReadOnly) {
        return Promise.reject("This field is read only and cannot be updated.");
      }

      const updateRecordFunction = isSupportal
        ? TenantAPI.modifyGlobalRecord
        : TenantAPI.modifyRecord;

      return updateRecordFunction(
        moduleAPIName,
        recordID,
        finalPayload,
        isSupportal
          ? sessionStorage.getItem("LastVisitedTenant") || undefined
          : undefined
      ).then((data) => {
        const record = data.data.data.data;
        const moduleAPIName = data.data.data.module;
        const flowChartData = data.data.data.flowChartData;
        const userRecordModuleActivity =
          data.data.data.userRecordModuleActivity || {};

        if (Object.keys(userRecordModuleActivity)?.length) {
          handleUpdateUserRecordRecommendedActivity(
            userRecordModuleActivity,
            updateUserRecordActivityCache
          );
        }

        if (useOptimisticUpdate) return record;

        handleUpdatedRecords(
          queryClient,
          moduleAPIName,
          record,
          tenantID ?? ""
        );

        handleAggregationUpdates(
          queryClient,
          tenantID!,
          moduleAPIName,
          finalPayload?.payload
        );

        // If FlowChartID was provided, update the FlowChart data.
        if (flowChartData) {
          queryClient.setQueryData(
            [tenantID, "record", "Flows", _FlowChartID],
            (prev: any) => ({
              ...(prev || {}),
              FlowChartNodes: flowChartData.nodes,
              FlowChartEdges: flowChartData.edges,
            })
          );
        }

        return record;
      });
    },

    mutationKey: ["updateRecord", theRecordID],
    onMutate: async ({
      field,
      recordID,
      payload,
      subitemID,
      moduleAPIName,
      originalRecord,
    }) => {
      if (!useOptimisticUpdate) return;

      const finalPayload = handleSubitem(field, payload, subitemID);

      const previousRecord = queryClient.getQueryData([
        tenantID,
        "record",
        moduleAPIName,
        recordID,
      ]);

      const updatedRecord = {
        ...(previousRecord || originalRecord),
        ...(finalPayload?.payload || {}),
      };

      handleUpdatedRecords(
        queryClient,
        moduleAPIName,
        updatedRecord,
        tenantID ?? ""
      );

      return { previousRecord };
    },
    // Reset Record on Error.
    onError: (err, { moduleAPIName, onError }, context) => {
      newToastEvent("error", getErrorMessage(err));

      callFunctionIfExists(onError);

      if (!useOptimisticUpdate) return; // Optimistic update is disabled.

      handleUpdatedRecords(
        queryClient,
        moduleAPIName,
        (context as any)?.previousRecord,
        tenantID ?? ""
      );
    },
    onSuccess: (updatedRecord, { onSuccess }) => {
      callFunctionIfExists(onSuccess, updatedRecord);

      // if (viewMode === "Timeline") {
      //   return queryClient.invalidateQueries({
      //     queryKey: ["records", moduleAPIName],
      //   });
      // }
    },
  });

  return {
    updateRecord: updateRecord.mutate,
    recordUpdating: updateRecord.isPending,
    updateRecordError: updateRecord.error
      ? getErrorMessage(updateRecord.error)
      : null,
  };
};

export default useUpdateRecord;
